import { loggingUtility } from '@/utils/loggingUtility';
import {
  APP_SUBDOMAIN,
  buildMarketingUrl,
  getSubdomain,
} from '@/utils/subdomainUtils';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

// Create and configure the Axios instance
export const createAxiosClient = (): AxiosInstance => {
  const apiClient = axios.create({
    baseURL: '/api',
    timeout: 120000, // 120 seconds - matches AWS App Runner max request timeout
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Required for cookie-based authentication
  });

  // Request interceptor to add subdomain header for routing
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Extract subdomain from current hostname for backend routing
      const currentHostname = window.location.hostname;
      const subdomain = currentHostname.split('.')[0];
      config.headers['X-Organization-Subdomain'] = subdomain;

      return config;
    },
    (error: AxiosError) => {
      loggingUtility.error('Request error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle errors and extract data
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      return response; // Return the full response object
    },
    (error: AxiosError) => {
      // Handle 301/302/307 redirects - backend validation failed (org/resource mismatch, inactive projects)
      if (
        error.response?.status === 301 ||
        error.response?.status === 302 ||
        error.response?.status === 307
      ) {
        // Get redirect URL from response headers or body, fallback to marketing site
        const redirectUrl =
          error.response.headers?.location ||
          (error.response.data as { redirectUrl?: string })?.redirectUrl ||
          buildMarketingUrl();

        window.location.href = redirectUrl;
        return new Promise(() => {});
      }

      if (error.response?.status === 401) {
        // Don't auto-redirect for auth endpoints - let the application handle those
        // This prevents race conditions during session restore and allows proper routing
        const isAuthEndpoint = error.config?.url?.startsWith('/auth/');

        // Don't redirect if already on login page - prevents redirect loops
        const isOnLoginPage = window.location.pathname === '/login';

        if (!isAuthEndpoint && !isOnLoginPage) {
          const currentUrl = window.location.pathname + window.location.search;
          const returnUrl = encodeURIComponent(currentUrl);
          const subdomain = getSubdomain();

          // On app subdomain, redirect to provider selection page (/login)
          // On organization subdomains, redirect directly to Cognito
          if (subdomain === APP_SUBDOMAIN) {
            window.location.href = `/login?returnUrl=${returnUrl}`;
          } else {
            window.location.href = `/api/auth/cognito/login?returnUrl=${returnUrl}`;
          }
        }
      }

      if (error.response) {
        loggingUtility.error('Error response:', error.response);
      } else if (error.request) {
        loggingUtility.error('Error request:', error.request);
      } else {
        loggingUtility.error('Error message:', error.message);
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
};

// Generic API methods with proper typing
export class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = createAxiosClient();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T>(url: string, config?: any): Promise<T> {
    try {
      const response = await this.client.get<T>(url, config);
      // For blob/text responses, return the full response so caller can access headers and raw data
      // We use 'unknown' as intermediate step to avoid unsafe direct cast
      if (config?.responseType === 'blob' || config?.responseType === 'text') {
        return response as unknown as T;
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.put<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.patch<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete<T>(url: string, config?: any): Promise<T> {
    try {
      const response = await this.client.delete<T>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      // Extract meaningful error message from backend response
      let errorMessage = 'An unexpected error occurred';

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (typeof error.response.data.message === 'string') {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return new Error(errorMessage);
    }

    return error instanceof Error ? error : new Error('Unknown error occurred');
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
